import { Trash01, Plus, Copy04, Container } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import VisualizeUnit from 'src/app/MyQuote/Modals/VisualizeUnit';
import ButtonCmp from 'src/components/ButtonCmp';
import InputText from 'src/components/InputText/InputText';
import Radio from 'src/components/Radio/Radio';
import SelectBox from 'src/components/SelectBox/SelectBox';
import Toggle from 'src/components/Toggle/Toggle';
import TooltipCmp from 'src/components/TooltipCmp';
import { MEASUREMENT } from 'src/constants/common';
import { calculateLinearFootage, formatLinearFootage, getCalculatedFreightClass, getLabelByValue, getStackingBlockedMessage, isStackingBlocked } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import {
    addQuoteConsigneeDetails as addQuoteConsigneeDetailsSelector,
    addQuoteServiceType as addQuoteServiceTypeSelector,
    addQuoteShipmentDetails as addQuoteShipmentDetailsSelector,
    addQuoteShipperDetails as addQuoteShipperDetailsSelector,
    addQuoteStopCount as addQuoteStopCountSelector,
    addQuoteStops as addQuoteStopsSelector,
} from '../../AddQuote.slice';
import { buildStopOptions } from '../../multiStop.helpers';

export const MAX_LINE_ITEMS = 15;

export interface IDimension {
    id: number | null;
    handlingUnit: number | string | undefined;
    handlingUnitNo: number | string | undefined;
    weight: number | string | undefined;
    itemLength: number | string | undefined;
    width: number | string | undefined;
    height: number | string | undefined;
    totalWeight: number | string | undefined;
    isStack: boolean | string | undefined;
    commodity: string | undefined;
    freightClass: number | string | undefined;
    itemClassForDayross: number | string | undefined;
    // Multi-stop route (Task 7) — which stop this item boards/departs at. null on classic
    // 2-stop quotes (routing selectors aren't rendered, so these stay at their default and
    // validateItemRouting/the payload builder fall back to stop 1 / final stop).
    pickupStopPosition: number | null;
    dropStopPosition: number | null;
}

const initDimensionData: IDimension = {
    id: null,
    handlingUnit: 1,
    handlingUnitNo: 0,
    weight: 0,
    itemLength: 0,
    width: 0,
    height: 0,
    totalWeight: 0,
    isStack: true,
    freightClass: 0,
    itemClassForDayross: 0,
    commodity: null,
    pickupStopPosition: null,
    dropStopPosition: null,
};

interface LineItemProps {
    isDimensionRequired?: boolean;
}

const LineItem = ({ isDimensionRequired = false }: LineItemProps) => {
    const { t } = useTranslation();
    const { handlingUnitOptions, commodityOptions } = useSelector((state: any) => state.basic);
    // AddQuote uses serviceType directly (string like 'ltl'/'ftl') instead of quoteDetails.classification
    const serviceType = useSelector(addQuoteServiceTypeSelector);
    const shipmentDetails = useSelector(addQuoteShipmentDetailsSelector);
    // Multi-stop route (Task 7) — stopCount/stops + shipper/consignee cities drive the per-item
    // pick/drop routing selectors below. On classic 2-stop quotes stopCount is 2 and the
    // selectors are never rendered (see the `stopCount > 2` guard further down).
    const stopCount = useSelector(addQuoteStopCountSelector);
    const stops = useSelector(addQuoteStopsSelector);
    const shipperDetails = useSelector(addQuoteShipperDetailsSelector);
    const consigneeDetails = useSelector(addQuoteConsigneeDetailsSelector);
    const { control, watch, setValue, trigger, formState, getValues, clearErrors } = useFormContext();
    const { isSubmitted } = formState;
    const [currentData, setCurrentData] = useState<{
        index: number;
        data: any;
    } | null>(null);

    const {
        fields: dimensionsOptionFields,
        append: appendOption,
        remove: removeOption,
    } = useFieldArray({
        control,
        name: 'dimensions',
        keyName: 'uuid',
    });

    const weightMeasurement = watch('weightMeasurement');
    const lengthMeasurement = watch('lengthMeasurement');
    const lengthMeasurementLabel = getLabelByValue(watch('lengthMeasurement'));
    const dimensions = watch('dimensions');
    const primaryEquipmentType = shipmentDetails?.primaryEquipmentType ?? '';
    const primaryEquipmentTypeFullName = shipmentDetails?.primaryEquipmentTypeFullName ?? '';

    // User decision (final) — the pick/drop routing row (below) shows as soon as ANY location in
    // the route — Shipper, any middle Stop, or Consignee — has an address, and hides only while
    // NONE of them do. Checks `city` (not fullAddress) because buildStopOptions below — the actual
    // consumer this row depends on — builds its labels from shipperCity/consigneeCity/stop city,
    // never fullAddress; a saved address picked via "Find or add a shipper/consignee" can have city
    // populated with an empty fullAddress (that field is only reliably set for a freshly
    // Google-autocompleted pick).
    //
    // Doesn't affect validation either way — pickupStopPosition/dropStopPosition aren't wired to
    // any yup rule (no <Controller> below); validateItemRouting defaults unset values to
    // pickup=stop1/drop=last-stop on submit, same as if the row had never been shown.
    const hasAnyLocationSelected = useMemo(
        () => !!shipperDetails?.shipperAddress?.city || !!consigneeDetails?.consigneeAddress?.city || Object.values(stops || {}).some((stop) => !!stop?.stopAddress?.city),
        [shipperDetails?.shipperAddress?.city, consigneeDetails?.consigneeAddress?.city, stops]
    );

    // Multi-stop route (Task 7) — option lists for the per-item pick/drop SelectBoxes.
    // shipperCity/consigneeCity source stop 1 / stop N; middle-stop cities come from `stops`.
    const { pickupOptions, dropOptions } = useMemo(
        () =>
            buildStopOptions(t, {
                stopCount,
                shipperCity: shipperDetails?.shipperAddress?.city,
                shipperState: shipperDetails?.shipperAddress?.state,
                shipperStateCode: shipperDetails?.shipperAddress?.stateCode,
                shipperCompanyName: shipperDetails?.shipperCompanyName,
                consigneeCity: consigneeDetails?.consigneeAddress?.city,
                consigneeState: consigneeDetails?.consigneeAddress?.state,
                consigneeStateCode: consigneeDetails?.consigneeAddress?.stateCode,
                consigneeCompanyName: consigneeDetails?.consigneeCompanyName,
                stops,
            }),
        [
            t,
            stopCount,
            shipperDetails?.shipperAddress?.city,
            shipperDetails?.shipperAddress?.state,
            shipperDetails?.shipperAddress?.stateCode,
            shipperDetails?.shipperCompanyName,
            consigneeDetails?.consigneeAddress?.city,
            consigneeDetails?.consigneeAddress?.state,
            consigneeDetails?.consigneeAddress?.stateCode,
            consigneeDetails?.consigneeCompanyName,
            stops,
        ]
    );

    useEffect(() => {
        // primaryEquipmentType lives on the Shipment Details form — use it directly (not watch()) so the correct equipment bucket drives the calculation.
        const calLinearFootage = formatLinearFootage(calculateLinearFootage(dimensions, primaryEquipmentType, false, MEASUREMENT.WEIGHT1?.value, weightMeasurement)) || null;
        setValue('linearFootage', calLinearFootage);

        if (isSubmitted) {
            trigger('linearFootage');
        }
    }, [
        primaryEquipmentType,
        weightMeasurement,
        JSON.stringify(
            dimensions?.map((item: any) => ({
                handlingUnitNo: item.handlingUnitNo,
                width: item.width,
                height: item.height,
                itemLength: item.itemLength,
                weight: item.weight,
                isStack: item.isStack,
            }))
        ),
    ]);

    // Force isStack=true (Do Not Stack ON) on any row whose height × 2 exceeds the bucket's stacking ceiling. Keeps form state aligned with the disabled toggle so saved data is consistent. Re-fires on measurement change because cm vs in changes the effective inches value.
    useEffect(() => {
        dimensions?.forEach((dim: any, idx: number) => {
            if (isStackingBlocked(primaryEquipmentType, dim?.height, weightMeasurement) && !dim?.isStack) {
                setValue(`dimensions.${idx}.isStack`, true);
            }
        });
    }, [primaryEquipmentType, weightMeasurement, JSON.stringify(dimensions?.map((d: any) => ({ h: d?.height, s: d?.isStack })))]);

    const handleOption = (onChange: any, name: string) => (e: any) => {
        onChange(e?.value);
        clearErrors(name);
    };

    const handleRemove = (index: number) => (e: any) => {
        e.stopPropagation();

        // For required equipment types with only one item, clear values instead of removing
        if (isDimensionRequired && dimensionsOptionFields.length === 1) {
            setValue('dimensions', [initDimensionData]);

            if (isSubmitted) {
                setTimeout(() => {
                    trigger('dimensions');
                }, 0);
            }
        } else if (dimensionsOptionFields.length > 1) {
            removeOption(index);

            // Trigger validation after removing if form was already submitted
            if (isSubmitted) {
                // Use setTimeout to trigger after the removal has been processed
                setTimeout(() => {
                    trigger('dimensions');
                }, 0);
            }
        } else {
            clearErrors('dimensions');

            // For LTL, allow removing the last item (show empty state with only Add button)
            if (serviceType === 'ltl') {
                setValue('dimensions', []);
            } else {
                setValue('dimensions', [initDimensionData]);
            }
        }
    };

    const handleAdd = (e?: any) => {
        e?.stopPropagation();

        if (dimensionsOptionFields.length >= MAX_LINE_ITEMS) {
            WalToast.error(t('Orders.Max_Line_Items_Reached') as string);

            return;
        }

        const newOption: IDimension = initDimensionData;
        appendOption(newOption);
    };

    const handleDuplicate = (index: number) => (e: any) => {
        e.stopPropagation();

        if (dimensionsOptionFields.length >= MAX_LINE_ITEMS) {
            WalToast.error(t('Orders.Max_Line_Items_Reached') as string);

            return;
        }

        const fieldValues = watch(`dimensions.${index}`);
        appendOption({ ...fieldValues, id: null });
    };

    const handleInputChange = (index: number) => (event: any) => {
        const lineItemData = watch(`dimensions.${index}`);
        const { target, type: eventType } = event;
        let { name, value, type } = target;

        // Clear error for this field when user starts typing
        clearErrors(`dimensions.${index}.${name}`);

        // Clear errors for both weight and totalWeight when either changes
        if (name === 'weight' || name === 'totalWeight') {
            clearErrors(`dimensions.${index}.weight`);
            clearErrors(`dimensions.${index}.totalWeight`);
        }

        if (type === 'number') {
            value = value ? parseFloat(value) : eventType === 'blur' ? 0 : value;
        }

        let totalWeight = lineItemData.totalWeight || 0;
        let weight = lineItemData.weight || 0;
        let handlingUnitNo = lineItemData.handlingUnitNo || 0;

        // If handlingUnitNo or weight changes → update totalWeight = handlingUnitNo * weight
        if (name === 'handlingUnitNo') {
            handlingUnitNo = value;

            if (handlingUnitNo > 0 && weight > 0) {
                totalWeight = handlingUnitNo * weight;

                if (Number.isInteger(totalWeight)) {
                    totalWeight = parseInt(totalWeight);
                } else {
                    totalWeight = Math.floor(totalWeight * 100) / 100;
                }
            } else {
                totalWeight = 0;
            }
        } else if (name === 'weight') {
            weight = value;

            if (handlingUnitNo > 0 && weight > 0) {
                totalWeight = handlingUnitNo * weight;

                if (Number.isInteger(totalWeight)) {
                    totalWeight = parseInt(totalWeight);
                } else {
                    totalWeight = Math.floor(totalWeight * 100) / 100;
                }
            } else {
                totalWeight = 0;
            }
        } else if (name === 'totalWeight') {
            // If totalWeight changes → update weight = totalWeight / handlingUnitNo
            totalWeight = typeof value === 'number' ? value : parseFloat(value) || 0;

            if (handlingUnitNo > 0 && totalWeight > 0) {
                // Unit weight input is integer-only (isAllowDecimal={false}), so the derived
                // value must be an integer too — otherwise the field shows a decimal the user
                // can't type and further edits are rejected by the input regex.
                weight = Math.floor(totalWeight / handlingUnitNo);
            } else {
                weight = 0;
            }
        }

        // Handle legacy 'weight' field for backward compatibility
        if (name === 'weight') {
            weight = value;

            if (handlingUnitNo > 0 && weight > 0) {
                totalWeight = handlingUnitNo * weight;

                if (Number.isInteger(totalWeight)) {
                    totalWeight = parseInt(totalWeight);
                } else {
                    totalWeight = Math.floor(totalWeight * 100) / 100;
                }
            } else {
                totalWeight = 0;
            }
        }

        if (isNaN(totalWeight) || !isFinite(totalWeight)) {
            totalWeight = 0;
        }

        if (isNaN(weight) || !isFinite(weight)) {
            weight = 0;
        }

        // Ensure totalWeight is a number before calling toFixed
        const totalWeightNumber = typeof totalWeight === 'number' ? totalWeight : parseFloat(totalWeight) || 0;

        setValue(`dimensions.${index}.${name}`, value);
        setValue(`dimensions.${index}.totalWeight`, parseFloat(totalWeightNumber.toFixed(2)));
        setValue(`dimensions.${index}.weight`, typeof weight === 'number' && !isNaN(weight) ? parseFloat(weight.toFixed(2)) : weight || 0);
        // Also update weight field for backward compatibility
        setValue(`dimensions.${index}.weight`, typeof weight === 'number' && !isNaN(weight) ? parseFloat(weight.toFixed(2)) : weight || 0);

        if (isSubmitted) {
            trigger(`dimensions.${index}.${name}`);
            trigger(`dimensions.${index}.totalWeight`);
            trigger(`dimensions.${index}.weight`);
        }
    };

    const handleChangeWithErrorClear = (index: number, fieldName: string) => (onChange: any) => (e: any) => {
        // Clear error for this field when user starts typing
        clearErrors(`dimensions.${index}.${fieldName}`);
        // Handle SelectBox case where value is in e.value
        onChange(e?.value !== undefined ? e.value : e);

        if (isSubmitted) {
            trigger(`dimensions.${index}.${fieldName}`);
        }
    };

    const checkFreightClass = (index: number) => (event: any) => {
        const lineItemData = watch(`dimensions.${index}`);
        const { target, type: eventType } = event;
        let { name, value, type } = target;

        // Clear error for this field when user starts typing
        clearErrors(`dimensions.${index}.${name}`);

        value = type === 'number' ? (value ? parseFloat(value) : eventType === 'blur' ? 0 : value) : value;
        let freightClass = lineItemData.freightClass;
        let itemClassForDayross = lineItemData.itemClassForDayross;
        let weight = lineItemData.weight;
        let width = lineItemData.width;
        let height = lineItemData.height;
        let itemLength = lineItemData.itemLength;

        if (weight && width && itemLength && height) {
            const freightData = getCalculatedFreightClass({
                itemLength: itemLength || 0,
                width: width || 0,
                height: height || 0,
                weight: weight || 0,
                lengthUnit: lengthMeasurement,
                weightUnit: weightMeasurement,
            });

            freightClass = freightData?.freightClass;
            itemClassForDayross = freightData?.itemClassForDayross;
        } else {
            freightClass = 0;
        }
        setValue(`dimensions.${index}.${name}`, value);
        setValue(`dimensions.${index}.freightClass`, freightClass);
        setValue(`dimensions.${index}.itemClassForDayross`, itemClassForDayross);

        if (freightClass) {
            clearErrors(`dimensions.${index}.freightClass`);
        }

        if (isSubmitted) {
            trigger(`dimensions.${index}.${name}`);
        }
    };

    const handleVisualizeUnit = (index: number) => (e: any) => {
        e.stopPropagation();
        setCurrentData({
            ...{ index: index, data: getValues(`dimensions.${index}`) },
        });
    };

    const handleVisualizeUnitClose = () => {
        setCurrentData(null);
    };

    const handleVisualizeUnitSubmit = (index: number, data: any) => {
        const fieldArray = ['itemLength', 'width', 'height', 'handlingUnit'];
        fieldArray.forEach((field) => {
            setValue(`dimensions.${index}.${field}`, data[field]);
        });
        handleVisualizeUnitClose();
    };

    const handleMeasurementChange = (onChange: any) => (event: any) => {
        const { value } = event.target;

        if (value === MEASUREMENT.WEIGHT1.value) {
            setValue('lengthMeasurement', MEASUREMENT.LENGTH1.value);
        } else {
            setValue('lengthMeasurement', MEASUREMENT.LENGTH2.value);
        }
        onChange(value);
    };

    return (
        <>
            <div className="space-y-5 w-full">
                <div className="stepper-content-container ">
                    <div className="stepper-lable-wrap">
                        <label className="stepper-label-common">{t('Common.Dimensions')}</label>
                    </div>

                    <div className="flex items-center gap-x-4 justify-end flex-1">
                        <Controller
                            control={control}
                            name="weightMeasurement"
                            render={({ field: { onChange, value } }) => (
                                <div className="flex items-center gap-4">
                                    <Radio
                                        onChangeFunc={handleMeasurementChange(onChange)}
                                        id="lbs"
                                        value={MEASUREMENT.WEIGHT1.value}
                                        checked={value === MEASUREMENT.WEIGHT1.value}
                                        labelName={t('Common.Imperial_(lbs)')}
                                        labelHtmlFor="lbs"
                                        radioLabelClassName="!normal-case"
                                    />

                                    <Radio
                                        onChangeFunc={handleMeasurementChange(onChange)}
                                        checked={value === MEASUREMENT.WEIGHT2.value}
                                        value={MEASUREMENT.WEIGHT2.value}
                                        id="kgs"
                                        labelName={t('Common.Metric_(kgs)')}
                                        labelHtmlFor="kgs"
                                        radioLabelClassName="normal-case"
                                    />
                                </div>
                            )}
                        />
                    </div>
                </div>

                {dimensionsOptionFields.length === 0 ? (
                    <div className="field-without-top-border rounded-lg gap-x-4 w-full justify-center flex-col items-center ">
                        <div className="border rounded-xl w-full border-gray500 border-dashed items-center bg-white flex justify-center min-h-[123px]">
                            <ButtonCmp className="btn_secondary_black" onClick={handleAdd} icon={<Plus className="w-4 h-4" />}>
                                {t('Common.Add_Line_Items')}
                            </ButtonCmp>
                        </div>
                    </div>
                ) : (
                    <>
                        <div data-field-anchor="dimensions" className="flex flex-col gap-y-4">
                            {dimensionsOptionFields.map((item: any, index) => (
                                <div data-field-anchor={`dimensions.${index}`} className={`flex xl:flex-row flex-col animate-field-item`} key={item.uuid}>
                                    <div className={`stepper-content-container`}>
                                        <div className="rounded-lg overflow-hidden border border-utilityGray200">
                                            <div className="bg-utilityGray300 flex items-center gap-x-2 p-3 border-b border-utilityGray200">
                                                <label className="text-grayLight900 text-xs font-medium flex-1">
                                                    {t('Common.Line_Item')} {index + 1}
                                                </label>
                                                <div className="flex items-center">
                                                    <Controller
                                                        name={`dimensions.${index}.isStack`}
                                                        control={control}
                                                        render={({ field: { value, onChange } }) => {
                                                            const blocked = isStackingBlocked(primaryEquipmentType, dimensions?.[index]?.height, weightMeasurement);
                                                            const blockedMessage = getStackingBlockedMessage(primaryEquipmentType, dimensions?.[index]?.height, weightMeasurement);

                                                            const toggleEl = (
                                                                <Toggle
                                                                    isChecked={blocked || value}
                                                                    onChange={onChange}
                                                                    isDisabled={blocked}
                                                                    labelClassName={`flex items-center`}
                                                                    switchClassName={blocked ? 'cursor-not-allowed' : 'cursor-pointer'}
                                                                    className={blocked ? 'pointer-events-none' : ''}
                                                                    name="isStack"
                                                                    label={t('QuotingHub.Common.Do_not_stack')}
                                                                    labelPosition="left"
                                                                    labelTextClassName={`!text-xs text-nowrap`}
                                                                    id={`doNotStack-${index}`}
                                                                />
                                                            );

                                                            return blocked ? (
                                                                <TooltipCmp message={blockedMessage}>
                                                                    <div className="inline-block">{toggleEl}</div>
                                                                </TooltipCmp>
                                                            ) : (
                                                                toggleEl
                                                            );
                                                        }}
                                                    />
                                                </div>
                                                <TooltipCmp message={t('Common.Duplicate')} childrenClassName="!flex">
                                                    <ButtonCmp className="btn-link-secondary" size="none" onClick={handleDuplicate(index)}>
                                                        <Copy04 className="w-4 h-4" />
                                                    </ButtonCmp>
                                                </TooltipCmp>
                                                <TooltipCmp message={t('Common.Visualize_Unit')} childrenClassName="!flex">
                                                    <ButtonCmp className="btn-link-secondary" size="none" onClick={handleVisualizeUnit(index)}>
                                                        <Container className="w-4 h-4" />
                                                    </ButtonCmp>
                                                </TooltipCmp>
                                                <TooltipCmp message={t('Common.Delete')} childrenClassName="!flex">
                                                    <ButtonCmp className="btn-link-secondary" size="none" onClick={handleRemove(index)}>
                                                        <Trash01 className="w-4 h-4" />
                                                    </ButtonCmp>
                                                </TooltipCmp>
                                            </div>
                                            <div className="w-full stepper-lable-content-wrap p-3 flex flex-wrap gap-y-3 gap-x-3 [&>*]:w-[calc(20%_-_10px)]">
                                                <Controller
                                                    name={`dimensions.${index}.handlingUnit`}
                                                    control={control}
                                                    render={({ field: { value, onChange, name }, fieldState: { error } }) => (
                                                        <SelectBox
                                                            label={t('Common.Handling_unit')}
                                                            required
                                                            labelClassName="form_label block mb-1.5"
                                                            name="handlingUnit"
                                                            id="handlingUnit"
                                                            className={`form_control`}
                                                            placeholder={t('Common.Handling_unit')}
                                                            options={handlingUnitOptions}
                                                            onChangeFunc={handleOption(onChange, name)}
                                                            isError={!!error?.message}
                                                            value={handlingUnitOptions.find((handlingUnitOption: any) => handlingUnitOption.value === parseInt(value) || '')}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.handlingUnitNo`}
                                                    control={control}
                                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                                        <InputText
                                                            required
                                                            label={t('Orders.NE_of_HU')}
                                                            labelClassName="form_label block mb-1.5"
                                                            inputName="handlingUnitNo"
                                                            inputType="number"
                                                            maxLength={3}
                                                            isLoading={false}
                                                            isAllowDecimal={false}
                                                            onChangeFunc={handleInputChange(index)}
                                                            onBlur={(e: any) => {
                                                                if (!e.target.value) {
                                                                    onChange(0);
                                                                }
                                                            }}
                                                            isError={!!error?.message}
                                                            shouldFormatNumber={true}
                                                            placeholder="0"
                                                            value={value || ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.itemLength`}
                                                    control={control}
                                                    render={({ field: { value }, fieldState: { error } }) => (
                                                        <InputText
                                                            label={
                                                                <>
                                                                    {t('QuotingHub.QuotingDashboard.Length')}
                                                                    &nbsp;(
                                                                    {lengthMeasurementLabel?.toLowerCase()})
                                                                </>
                                                            }
                                                            labelClassName="form_label block mb-1.5"
                                                            inputName="itemLength"
                                                            inputType="number"
                                                            placeholder="0"
                                                            value={value || ''}
                                                            isAllowDecimal={false}
                                                            maxLength={4}
                                                            shouldFormatNumber={true}
                                                            onChangeFunc={handleInputChange(index)}
                                                            onBlur={checkFreightClass(index)}
                                                            inputValueUnitType={lengthMeasurementLabel?.toLowerCase()}
                                                            required
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            isIconError={!!error?.message}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.width`}
                                                    control={control}
                                                    render={({ field: { value }, fieldState: { error } }) => (
                                                        <InputText
                                                            label={
                                                                <>
                                                                    {t('QuotingHub.QuotingDashboard.Width')}
                                                                    &nbsp;(
                                                                    {lengthMeasurementLabel?.toLowerCase()})
                                                                </>
                                                            }
                                                            labelClassName="form_label block mb-1.5"
                                                            inputName="width"
                                                            inputType="number"
                                                            placeholder="0"
                                                            value={value || ''}
                                                            onChangeFunc={handleInputChange(index)}
                                                            onBlur={checkFreightClass(index)}
                                                            required
                                                            isAllowDecimal={false}
                                                            maxLength={4}
                                                            shouldFormatNumber={true}
                                                            inputValueUnitType={lengthMeasurementLabel?.toLowerCase()}
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            isIconError={!!error?.message}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.height`}
                                                    control={control}
                                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                                        <InputText
                                                            label={
                                                                <>
                                                                    {t('QuotingHub.QuotingDashboard.Height')}
                                                                    &nbsp;(
                                                                    {lengthMeasurementLabel?.toLowerCase()})
                                                                </>
                                                            }
                                                            isLoading={false}
                                                            labelClassName="form_label block mb-1.5"
                                                            inputType="number"
                                                            inputName="height"
                                                            placeholder="0"
                                                            isAllowDecimal={false}
                                                            maxLength={4}
                                                            shouldFormatNumber={true}
                                                            inputValueUnitType={lengthMeasurementLabel?.toLowerCase()}
                                                            value={value || ''}
                                                            onChangeFunc={handleChangeWithErrorClear(index, 'height')(onChange)}
                                                            onBlur={checkFreightClass(index)}
                                                            required
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            isIconError={!!error?.message}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.weight`}
                                                    control={control}
                                                    render={({ field: { value }, fieldState: { error } }) => (
                                                        <InputText
                                                            label={
                                                                <>
                                                                    {t('QuotingHub.QuotingDashboard.Unit_Weight')}
                                                                    &nbsp;(
                                                                    {weightMeasurement?.toLowerCase()})
                                                                </>
                                                            }
                                                            labelClassName="form_label block mb-1.5"
                                                            inputName="weight"
                                                            inputType="number"
                                                            placeholder="0"
                                                            value={value || ''}
                                                            onChangeFunc={handleInputChange(index)}
                                                            onBlur={checkFreightClass(index)}
                                                            required
                                                            isAllowDecimal={false}
                                                            maxLength={7}
                                                            shouldFormatNumber={true}
                                                            inputValueUnitType={weightMeasurement?.toLowerCase()}
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            isIconError={!!error?.message}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.totalWeight`}
                                                    control={control}
                                                    render={({ field: { value }, fieldState: { error } }) => (
                                                        <InputText
                                                            label={
                                                                <>
                                                                    {t('Common.Total_Weight')}
                                                                    &nbsp;(
                                                                    {weightMeasurement?.toLowerCase()})
                                                                </>
                                                            }
                                                            labelClassName="form_label block mb-1.5"
                                                            inputName="totalWeight"
                                                            inputType="number"
                                                            placeholder="0"
                                                            value={value || ''}
                                                            onChangeFunc={handleInputChange(index)}
                                                            onBlur={checkFreightClass(index)}
                                                            required
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            isIconError={!!error?.message}
                                                            isAllowDecimal={false}
                                                            maxLength={7}
                                                            shouldFormatNumber={true}
                                                            inputValueUnitType={weightMeasurement?.toLowerCase()}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.freightClass`}
                                                    control={control}
                                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                                        <InputText
                                                            label={t('QuotingHub.Common.Freight_Class')}
                                                            labelClassName="form_label block mb-1.5"
                                                            isLoading={false}
                                                            inputName="freightClass"
                                                            inputType="number"
                                                            value={value || ''}
                                                            placeholder="0"
                                                            maxLength={3}
                                                            required
                                                            onChangeFunc={handleChangeWithErrorClear(index, 'freightClass')(onChange)}
                                                            shouldFormatNumber={true}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.commodity`}
                                                    control={control}
                                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                        <SelectBox
                                                            parentClassName="!w-[calc(40%_-_6.4px)]"
                                                            placeholder={t('Common.Select_commodity')}
                                                            autoCompleteSelect={true}
                                                            name="commodity"
                                                            id={`commodity-${index}`}
                                                            className="form_control"
                                                            label={t('Common.Commodity')}
                                                            labelClassName="form_label block mb-1.5"
                                                            isSearchable={true}
                                                            options={commodityOptions || []}
                                                            value={
                                                                value
                                                                    ? commodityOptions?.find((e: any) => e?.value == value) || {
                                                                          value: value,
                                                                          label: value,
                                                                      }
                                                                    : null
                                                            }
                                                            onChangeFunc={handleChangeWithErrorClear(index, 'commodity')(onChange)}
                                                            isClearable={true}
                                                            onCreateOption={(e: any) => {
                                                                onChange(e);
                                                            }}
                                                            errorText={error?.message ? true : false}
                                                            isShowErrorMsg={false}
                                                        />
                                                    )}
                                                />
                                            </div>

                                            {/* Multi-stop route (Task 7) — per-item pick/drop routing. Gated on stopCount > 2 so
                                                classic 2-stop quotes render an identical row to before this feature (HARD GATE), and
                                                on hasAnyLocationSelected (user decision) so the row stays hidden only while NONE of
                                                Shipper/any Stop/Consignee has an address yet — shows as soon as any one of them does.
                                                No <Controller> here (unlike most cells) because there's no yup rule to wire up —
                                                validateItemRouting runs once on submit, so a plain watch/setValue pair (matching
                                                totalWeight/weight's direct-setValue pattern above) is enough. Rendered as its own
                                                bordered row below the field grid (design-only change) rather than inline in the
                                                [&>*]:w-[calc(20%_-_10px)] grid, matching the Edit Quote modal's Load Details layout. */}
                                            {stopCount > 2 && hasAnyLocationSelected && (
                                                <div className="border-t border-utilityGray200 mx-3 pb-3 pt-3 flex gap-x-3">
                                                    <SelectBox
                                                        parentClassName="flex-1"
                                                        label={t('MyQuote.Picks_Up_At')}
                                                        labelClassName="form_label block mb-1.5"
                                                        placeholder={t('MyQuote.Select_Pickup_Location')}
                                                        isClearable
                                                        name="pickupStopPosition"
                                                        id={`pickupStopPosition-${index}`}
                                                        className="form_control"
                                                        options={pickupOptions}
                                                        // Unset (null) renders the placeholder rather than falling back to a stop — the row's
                                                        // Delete/clear resets pickupStopPosition to null (initDimensionData) and that reset has to
                                                        // be visible, same as every other field it clears. An item left unset still routes
                                                        // shipper -> consignee on submit (validateItemRouting), exactly as before.
                                                        value={pickupOptions.find((option) => option.value === watch(`dimensions.${index}.pickupStopPosition`)) ?? null}
                                                        onChangeFunc={(option: any) => setValue(`dimensions.${index}.pickupStopPosition`, option?.value ?? null)}
                                                    />
                                                    <SelectBox
                                                        parentClassName="flex-1"
                                                        label={t('MyQuote.Delivers_To')}
                                                        labelClassName="form_label block mb-1.5"
                                                        placeholder={t('MyQuote.Select_Drop_Off_Location')}
                                                        isClearable
                                                        name="dropStopPosition"
                                                        id={`dropStopPosition-${index}`}
                                                        className="form_control"
                                                        options={dropOptions}
                                                        // Same as the pickup select above — null shows the placeholder so Delete/clear is visible.
                                                        value={dropOptions.find((option) => option.value === watch(`dimensions.${index}.dropStopPosition`)) ?? null}
                                                        onChangeFunc={(option: any) => setValue(`dimensions.${index}.dropStopPosition`, option?.value ?? null)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-x-3 w-full justify-end !mt-4 items-center stepper-content-container field-without-top-border animate-field-item">
                            <ButtonCmp className="btn-outline-primary" icon={<Plus className="w-4 h-4" />} onClick={handleAdd}>
                                {t('Common.Add_Line_Item')}
                            </ButtonCmp>
                        </div>
                    </>
                )}
            </div>
            {currentData && (
                <VisualizeUnit
                    currentData={currentData}
                    handleClose={handleVisualizeUnitClose}
                    weightMeasurement={weightMeasurement}
                    lengthMeasurement={lengthMeasurement}
                    isEnable={true}
                    parentFreightClass={getValues(`dimensions.${currentData.index}.freightClass`)}
                    unitArr={handlingUnitOptions}
                    isFormDataHaveKey={true}
                    handleVisualizeUnitSubmit={handleVisualizeUnitSubmit}
                    primaryEquipmentType={primaryEquipmentType ?? ''}
                    primaryEquipmentTypeFullName={primaryEquipmentTypeFullName ?? ''}
                />
            )}
        </>
    );
};

export default LineItem;
